import React from 'react';
import { FiBookOpen, FiUsers, FiCheckCircle, FiClock, FiMail, FiCalendar, FiTrendingUp } from 'react-icons/fi';

const PUCollegeGuide = () => {
  const features = [
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Stream Management (Science, Commerce, Arts)',
      description: 'PU colleges are where students make critical academic choices. The system must handle multiple streams—Science, Commerce, Arts—each with different subject combinations, lab requirements, and exam patterns. Stream management should be flexible enough to accommodate student choices and academic requirements.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Exam Preparation Tracking',
      description: 'PU college students are preparing for board exams and competitive exams simultaneously. The system should track both regular academic performance and competitive exam preparation progress. This dual tracking is essential for student success.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Subject Combinations and Electives',
      description: 'Students in PU colleges choose elective subjects based on their stream and career goals. The system must manage complex subject combinations, handle student preferences, and ensure timetable feasibility. No more manual spreadsheet juggling.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiCalendar className="w-6 h-6" />,
      title: 'Board Exam Integration',
      description: 'PU colleges must align with state or central board requirements. The system should handle board exam registrations, internal assessment scores, project submissions, and practical exam records. Integration with board processes reduces administrative workload significantly.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Attendance and Internal Assessment',
      description: 'Attendance in PU colleges is critical—not just for academic records but for eligibility for board exams. The system must track attendance meticulously, calculate internal assessment marks, and generate reports for board submission. Every 75% attendance cutoff matters.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Parent Communication for Teenagers',
      description: 'PU college students are teenagers—often less communicative with parents. The system needs to bridge this gap by keeping parents informed about attendance, performance, and upcoming deadlines. Early communication prevents parent anxieties.',
      color: 'bg-blue-50'
    }
  ];

  const challenges = [
    {
      title: 'Managing Student Choices Across Streams',
      description: 'Students frequently change subject combinations or streams, especially in the first few months. The system must handle these changes without disrupting timetables or academic records.'
    },
    {
      title: 'Balancing Academic and Competitive Exam Prep',
      description: 'PU students are preparing for board exams and competitive exams like JEE, NEET, or CLAT. The system should support both without overwhelming students or teachers.'
    },
    {
      title: 'Tracking Practical and Lab Work',
      description: 'Science students need regular lab work tracking. The system should manage practical exam schedules, lab attendance, and practical record submissions.'
    },
    {
      title: 'Board Compliance Requirements',
      description: 'Different boards have different requirements. The system must be flexible enough to handle state board, CBSE, or other board requirements without changing core functionality.'
    },
    {
      title: 'Staff Management Across Departments',
      description: 'PU colleges have specialized faculty for different streams. The system should manage faculty allocation, workload distribution, and department coordination seamlessly.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Pre-University colleges are a unique and critical part of India's education system. For students, these two years are transformative. They choose their academic streams, prepare for board exams, and begin competitive exam preparation. The decisions made in PU college shape career trajectories.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For educators and administrators, PU colleges are a unique challenge. <strong className="text-slate-900">Schools focus on foundation. Universities focus on specialization. PU colleges must do both.</strong> They must support multiple streams, complex subject choices, board exam requirements, and competitive exam preparation—all while managing adolescent students in a transitional phase of their lives.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        General school management systems often fall short for PU colleges. Here's why PU colleges need specialized ERPs and what features are essential.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">40,000+</div>
          <div className="text-sm text-slate-500">PU colleges across India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">70%</div>
          <div className="text-sm text-slate-500">Students take competitive exams</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">5+</div>
          <div className="text-sm text-slate-500">Subject combinations per student</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Essential Features for PU College Management</h2>

      {/* Features List */}
      {features.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Challenges Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Unique Challenges PU Colleges Face</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {challenges.map((item, i) => (
            <div key={i} className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-start gap-2">
                <span className="text-amber-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">School Software vs. PU College Software</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="text-sm font-semibold text-blue-700 mb-2">School Software (K-12)</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Focus on general education</span></li>
              <li className="flex items-start gap-2">• <span>Single curriculum</span></li>
              <li className="flex items-start gap-2">• <span>Simple subject management</span></li>
              <li className="flex items-start gap-2">• <span>Parent communication for younger children</span></li>
              <li className="flex items-start gap-2">• <span>Board exam at end of 10th</span></li>
            </ul>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <div className="text-sm font-semibold text-purple-700 mb-2">PU College Software</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Stream-based education (Science, Commerce, Arts)</span></li>
              <li className="flex items-start gap-2">• <span>Multiple subject combinations</span></li>
              <li className="flex items-start gap-2">• <span>Complex elective management</span></li>
              <li className="flex items-start gap-2">• <span>Parent communication for teenagers</span></li>
              <li className="flex items-start gap-2">• <span>Board exams + competitive exam prep</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for PU Colleges</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Stream Management</h4>
            <p className="text-xs text-slate-600">Handle Science, Commerce, Arts seamlessly</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📚</div>
            <h4 className="font-bold text-slate-900 text-sm">Subject Combinations</h4>
            <p className="text-xs text-slate-600">Manage complex elective choices with ease</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Board Integration</h4>
            <p className="text-xs text-slate-600">Seamless board exam registration and reporting</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Competitive Exam Prep</h4>
            <p className="text-xs text-slate-600">Track both board and competitive exam progress</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile First</h4>
            <p className="text-xs text-slate-600">Full functionality on smartphones</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Parent Communication</h4>
            <p className="text-xs text-slate-600">Keep parents informed in the language they prefer</p>
          </div>
        </div>
      </div>

      {/* Decision Checklist */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">PU College ERP Checklist</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it handle multiple streams?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Can it manage complex subject combinations?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it track both board and competitive exam prep?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Is attendance tracking board-compliant?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Can parents access student progress easily?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it support multiple Indian languages?</span>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">PU Colleges Need Specialized ERPs</h3>
        <p className="text-indigo-800 leading-relaxed">
          PU colleges are not schools. They're not degree colleges. They're unique institutions with unique needs. General purpose school management software simply cannot handle the complexity of stream management, subject combinations, board requirements, and competitive exam preparation that PU colleges require.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Choosing the right ERP for your PU college is an investment in your students' success. The right system reduces administrative burden, improves student outcomes, and prepares students for the board and competitive exams that will shape their futures.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon is built for PU colleges.</strong> Stream management. Subject combinations. Board integration. Competitive exam tracking. All in one platform. Ready to transform your PU college.
        </p>
      </div>
    </>
  );
};

export default PUCollegeGuide;